import { DEFAULT_AFFIDAVIT_URL_UPDATED } from 'lib/affidavits';
import { cdnIfy } from 'lib/helpers';
import { EOrganization, ERef, ESnapshotExists } from 'lib/types';
import {
  AffidavitTemplate,
  AffidavitTemplateV2
} from 'lib/types/affidavitTemplate';
import { getBooleanFlag } from 'utils/flags';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import {
  AffidavitTemplateDefaultOptions,
  getAffidavitTemplateDefaultStatus
} from '../statusHelpers';
import { loadAffidavitTemplateHTML } from '../htmlHelpers';

export type AffidavitTemplateEditDataV2 = {
  affidavitTemplateHTML: string;
  defaultStatus: AffidavitTemplateDefaultOptions;
  storagePath: string;
  publisher: ERef<EOrganization>;
  name: string;
  isColumnManaged: boolean;
  logoSource: AffidavitTemplateV2['logoSource'];
  logoHeightInInches?: number;
  blockWidthInInches?: number;
  fontSize?: number;
};

export const extractFormData = async (
  activeOrganization: ESnapshotExists<EOrganization>,
  affidavitTemplateRef: ERef<AffidavitTemplate> | undefined,
  affidavitTemplate: AffidavitTemplate
): Promise<AffidavitTemplateEditDataV2> => {
  const isNewTemplate = !affidavitTemplateRef;
  const isV2 = affidavitTemplate.version === 'v2024-03-25';

  /**
   * If this is a new template, or a legacy template being converted to v2, we
   * need to pull the default affidavit template HTML.
   *
   * Otherwise, we can use what's currently saved on the template.
   */
  const useColumnCDN = getBooleanFlag(LaunchDarklyFlags.ENABLE_COLUMN_CDN);
  const htmlUrl =
    isNewTemplate || !isV2
      ? DEFAULT_AFFIDAVIT_URL_UPDATED
      : cdnIfy(affidavitTemplate.storagePath, { useColumnCDN });

  const affidavitTemplateHTML = await loadAffidavitTemplateHTML(htmlUrl);

  return {
    affidavitTemplateHTML,
    defaultStatus: getAffidavitTemplateDefaultStatus(
      activeOrganization,
      affidavitTemplateRef
    ),
    storagePath: affidavitTemplate.storagePath,
    publisher: affidavitTemplate.publisher,
    name: affidavitTemplate.name,
    isColumnManaged: affidavitTemplate.isColumnManaged,
    logoSource: isV2 ? affidavitTemplate.logoSource : 'column',
    logoHeightInInches: isV2 ? affidavitTemplate.logoHeightInInches : undefined,
    blockWidthInInches: isV2 ? affidavitTemplate.blockWidthInInches : undefined,
    fontSize: isV2 ? affidavitTemplate.fontSize : undefined
  };
};
